import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { ImageState, ObjectMode, ObjectType } from '~/static/enums/object-type'
import { EditorObject, LayoutContext, ObjectState } from '@/utils/editor/object.class'
import { FillType } from '~/static/enums/components/color-input'
import { ActualVersion, ActualVersionNumber } from '~/utils/editor/versioning/image'
import { useImageStore } from '~/store/image'
import { WatchStopHandle } from 'vue'
import { assignNewColorAndAlpha } from '~/utils/ColorHelper'
import { createBrokenImagePlug, loadImageAndConvertToPng } from '~/utils/editor/helpers/ImageHelper'
import { calculateZoom, makeShadow } from '~/utils/editor/helpers/Helpers'
import { useAuth } from '~/store/auth'
import { BackgroundGenerationStatus } from '~/static/enums/image/background-generation-status'
import { Action } from '~/static/types/editor'
import { AnyActualProp } from '../versioning/migrators'

const COMPRESSED_IMAGE_WIDTH = 1400

export class ImageObject extends EditorObject {
  width: number = 0
  height: number = 0
  cropWidth!: number
  cropHeight!: number
  originalWidth!: number
  originalHeight!: number
  offsetX: number = 0
  offsetY: number = 0
  borderRadius: number = 0
  border: number = 1
  borderColor: string = 'rgba(0,0,0,1)'
  borderOpacity: number = 100
  borderDash: boolean = false
  borderExists: boolean = false
  shadow: boolean = false
  shadowColor: string = 'rgba(0,0,0,1)'
  shadowOpacity: number = 100
  shadowX: number = 0
  shadowY: number = 4
  shadowBlur: number = 4
  backRemoved: boolean = false
  originalUrl: string | null = null
  hasCorrection: boolean = false
  brightness: number = 0
  contrast: number = 0
  saturation: number = 0
  colorfulness: number = 0
  hue: number = 0
  hasOpacity: boolean = false
  opacity: number = 1
  isNeiro: boolean = false
  isEnhanced: boolean = false

  imageName: string = ''
  imageUrl: string = ''
  image!: HTMLImageElement

  mode: ObjectMode = ObjectMode.Editing
  dirtyImage: boolean = false

  /**
   * Параметр, обозначающий, что эта картинка была создана с багом, где параметры обводки,
   * тени и скругления зависели не от ширины объекта, а от оригинального размера фото
   */
  correctParamsRatio?: boolean

  constructor(props: ActualVersion) {
    super(ObjectType.Image)
    this.assignProperties(props)
  }

  load(mode: ObjectMode): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.imageUrl) {
        reject('Не задан url изображения')
        return
      }
      this.mode = mode
      this.dirtyImage = false
      this.loadImageWithFilters()
        .then(resolve)
        .catch(reject)
    })
  }

  applyInitialLayout(context: LayoutContext): void {
    // В новых картинках нет бага
    this.correctParamsRatio = true

    if (this.width !== 0 && this.height !== 0) {
      return
    }

    const imageScale = calculateZoom(this.cropWidth, this.cropHeight, context.width, context.height)

    this.width = this.cropWidth * imageScale
    this.height = this.cropHeight * imageScale
  }

  applyDefaultName(): void {
  }

  set(props: ActualVersion): Promise<void> {
    this.assignProperties(props)
    return this.applyProps()
  }

  protected assignProperties(props: ActualVersion): void {
    this.dirtyImage = (
      !(ImageState.Filter in this.states) &&
      (this.hasCorrection || props.hasCorrection) && (
        this.brightness !== (props.brightness ?? 0) ||
        this.contrast !== (props.contrast ?? 0) ||
        this.saturation !== (props.saturation ?? 0) ||
        this.colorfulness !== (props.colorfulness ?? 0) ||
        this.hue !== (props.hue ?? 0) ||
        this.hasCorrection !== (props.hasCorrection ?? 0)
      )
    ) || (props.imageUrl !== undefined && this.imageUrl !== props.imageUrl) || this.dirtyImage

    super.assignProperties(props)

    const borderColor = assignNewColorAndAlpha(
      FillType.Solid,
      this.borderColor,
      this.borderOpacity,
      props.borderColor,
      props.borderOpacity,
    )
    this.borderColor = <string>borderColor.color
    this.borderOpacity = borderColor.opacity

    const shadowColor = assignNewColorAndAlpha(
      FillType.Solid,
      this.shadowColor,
      this.shadowOpacity,
      props.shadowColor,
      props.shadowOpacity,
    )
    this.shadowColor = <string>shadowColor.color
    this.shadowOpacity = shadowColor.opacity
  }

  protected applyProps(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.object) {
        this.setObject(new fabric.Rect())
      }

      const handler = async () => {
        await super.applyProps()

        this.object.set(this.getFabricObjectProps())

        this.dirtyImage = false
        this.object.setCoords()
      }

      if (this.dirtyImage) {
        this.loadImageWithFilters()
          .then(() => {
            handler().then(() => {
              resolve()
            })
          })
          .catch((error) => {
            handler().then(() => {
              reject(error ?? 'Loading image with filters failed')
            })
          })
      } else {
        handler().then(() => {
          resolve()
        })
      }
    })
  }

  getFabricObjectProps(): Partial<fabricTypes.RectProps> {
    const cropWidth = this.compressedCropWidth
    const cropHeight = this.compressedCropHeight

    const scaleX = this.width / cropWidth
    const scaleY = this.height / cropHeight

    let rx = this.borderRadius / scaleX
    let ry = this.borderRadius / scaleY
    let strokeWidth = this.borderExists ? this.border : 0
    let borderDash = 10
    let shadowX = this.shadowX
    let shadowY = this.shadowY

    // Для багованных карточек мы оставляем старые значения, чтобы карточки отображались так же, как при сохранении
    if (!this.correctParamsRatio) {
      // Но для сжатых изображений при скачивании были проблемы с тенью, рамкой и скруглением, поэтому мы их
      // дополнительно умножаем на отношение сжатых сторон на текущие. Вычислено по соотношению (аналогично для
      // всех параметров): editorBorderWidth / COMPRESSED_IMAGE_WIDTH = serverBorderWidth / originalWidth
      if (this.originalWidth > COMPRESSED_IMAGE_WIDTH) {
        const compressedHeight = this.originalHeight * COMPRESSED_IMAGE_WIDTH / this.originalWidth
        const xRatio = this.compressedCropWidth / COMPRESSED_IMAGE_WIDTH
        const yRatio = this.compressedCropHeight / compressedHeight

        rx = xRatio * this.borderRadius
        ry = yRatio * this.borderRadius
        strokeWidth = xRatio * strokeWidth
        borderDash = xRatio * borderDash
        shadowX = xRatio * shadowX
        shadowY = yRatio * shadowY
      } else {
        rx = this.borderRadius
        ry = this.borderRadius
      }
    }

    return {
      width: cropWidth,
      height: cropHeight,
      scaleX: scaleX,
      scaleY: scaleY,
      rx: rx,
      ry: ry,
      strokeWidth: strokeWidth,
      stroke: this.borderColor,
      strokeDashArray: this.borderDash ? [ borderDash ] : null,
      shadow: makeShadow({
        ...this,
        shadowX,
        shadowY,
        nonScaling: this.correctParamsRatio === true,
      }),
      fill: new fabric.Pattern({
        source: this.image as HTMLImageElement,
        repeat: 'no-repeat',
        offsetX: this.offsetX * this.image.width / this.originalWidth,
        offsetY: this.offsetY * this.image.height / this.originalHeight,
      }),
      opacity: this.hidden ? 0 : (this.hasOpacity ? this.opacity : 1),
      // Important for node render: if this isn't set, shadow ignores transparency
      objectCaching: true,
      // Для багованных карточек не делаем strokeUniform, иначе сохраненные карточки, где сохранено искажение рамки
      // для непропорциональных размеров, примут равные ширины для высоты и ширины, а это не то, что они делали
      strokeUniform: this.correctParamsRatio === true,
    }
  }

  onScaling(event: fabricTypes.ModifiedEvent): boolean {
    // Для карточек с багом мы сохраняем искажение при масштабировании
    // (иначе после масштабирования "сломанные" углы станут нормальными)
    if (this.correctParamsRatio === true) {
      // Подгонка скругления углов при ресайзе (вне зависимости от
      // масштаба скругление всегда должно выглядеть одинаково)

      this.width = parseInt('' + this.getRealObjectWidth())
      this.height = parseInt('' + this.getRealObjectHeight())

      const scaleX = this.width / this.compressedCropWidth
      const scaleY = this.height / this.compressedCropHeight

      this.object.set({
        rx: this.borderRadius / scaleX,
        ry: this.borderRadius / scaleY,
      })

      super.onScaling(event)
      return true
    }

    return super.onScaling(event)
  }

  afterModified(): boolean {
    this.width = parseInt('' + this.getRealObjectWidth())
    this.height = parseInt('' + this.getRealObjectHeight())
    return super.afterModified()
  }

  toObject(): object {
    const data = super.toObject()
    data.version = ActualVersionNumber

    if ('image' in data) {
      delete data.image
    }
    if ('crop' in data) {
      delete data.crop
    }
    if ('bluredRect' in data) {
      delete data.bluredRect
    }
    if ('visibleRect' in data) {
      delete data.visibleRect
    }
    if ('originalRenders' in data) {
      delete data.originalRenders
    }
    if ('dirtyImage' in data) {
      delete data.dirtyImage
    }
    if ('mode' in data) {
      delete data.mode
    }

    return data
  }

  getCanvasManagedProperties(): AnyActualProp {
    return Object.assign(super.getCanvasManagedProperties(), {
      width: this.width,
      height: this.height,
    })
  }

  get name(): string {
    return this.imageName
  }

  get bodyColour(): Rgba {
    return { r: 0, g: 0, b: 0, a: 1 }
  }

  getRealObjectWidth() {
    return this.object.scaleX * (this.object.group?.scaleX ?? 1) * this.object.width
  }

  getRealObjectHeight() {
    return this.object.scaleY * (this.object.group?.scaleY ?? 1) * this.object.height
  }

  onSelect() {
    super.onSelect()
  }

  onDoubleClick(): void {
    this.addState(ImageState.Crop, new CropState())
  }

  /**
   * Convert value of correction between -100 and 100 to value between -1 and 1
   * @param value Correction value between -100 and 100
   * @returns Correction value
   */
  calculateCorrectionFromValue(value: number) {
    return value / 100
  }

  /**
   * Applies filters to image
   */
  async loadImageWithFilters(): Promise<void> {
    return new Promise((resolve, reject) => {
      const filters: fabricTypes.filters.BaseFilter<string>[] = []
      if (this.hasCorrection) {
        if (this.brightness !== 0) {
          filters.push(
            new fabric.filters.Brightness({ brightness: this.calculateCorrectionFromValue(this.brightness) }),
          )
        }
        if (this.contrast !== 0) {
          filters.push(new fabric.filters.Contrast({ contrast: this.calculateCorrectionFromValue(this.contrast) }))
        }
        if (this.saturation !== 0) {
          filters.push(
            new fabric.filters.Saturation({ saturation: this.calculateCorrectionFromValue(this.saturation) }),
          )
        }
        if (this.colorfulness !== 0) {
          filters.push(new fabric.filters.Vibrance({ vibrance: this.calculateCorrectionFromValue(this.colorfulness) }))
        }
        if (this.hue !== 0) {
          filters.push(new fabric.filters.HueRotation({ rotation: this.calculateCorrectionFromValue(this.hue) }))
        }
      }

      loadImageAndConvertToPng(this.imageUrlWithMode)
        .then((img) => {
          const image = new fabric.FabricImage(img, {
            filters: filters,
          })
          this.image = <HTMLImageElement>image.getElement()
          const callback = () => {
            if (this.cropWidth === undefined && this.cropHeight === undefined) {
              this.cropWidth = this.originalWidth
              this.cropHeight = this.originalHeight
            }
            resolve()
          }
          if (this.originalWidth === undefined || this.originalHeight === undefined) {
            if (this.image.width === COMPRESSED_IMAGE_WIDTH) {
              loadImageAndConvertToPng(this.imageUrl)
                .then((img) => {
                  this.originalWidth = img.width
                  this.originalHeight = img.height
                  callback()
                })
                .catch(() => {
                  this.originalWidth = this.image.width
                  this.originalHeight = this.image.height
                  callback()
                })
            } else {
              // Если ширина не COMPRESSED_IMAGE_WIDTH - значит изображение не было сжато и нет смысла грузить оригинал
              this.originalWidth = this.image.width
              this.originalHeight = this.image.height
              callback()
            }
          } else {
            callback()
          }
        })
        .catch((e) => {
          console.log(e)
          createBrokenImagePlug(this.toObject())
            .then((dataUrl) => {
              loadImageAndConvertToPng(dataUrl)
                .then((image) => {
                  this.image = image
                  reject('Не удалось загрузить изображение. Пожалуйста, напишите в поддержку')
                })
                .catch((error) => {
                  reject(error ?? 'Unknown error on loading image')
                })
            })
        })
    })
  }

  get compressedCropWidth() {
    return this.image.width * (this.cropWidth / this.originalWidth)
  }

  get compressedCropHeight() {
    return this.image.height * (this.cropHeight / this.originalHeight)
  }

  get imageUrlWithMode() {
    return this.imageUrl + (this.mode === ObjectMode.Editing ? '?w=' + COMPRESSED_IMAGE_WIDTH : '')
  }

  get freezeEditor(): boolean {
    return Object.keys(this.states).includes(ImageState.Crop)
  }
}

export class ApplyfiltersState extends ObjectState {
  image: fabricTypes.FabricImage | null = null
  group: fabricTypes.Group | null = null
  removeAfterCreating: boolean = false

  onAdd(object: ImageObject): void {
    fabric.FabricImage.fromURL(object.imageUrlWithMode).then((image) => {
      const props = object.getFabricObjectProps()

      const cropWidth = props.width!
      const cropHeight = props.height!

      object.object.visible = false

      // Рамка. Отдельным объектом, так как если мы рамку применяем к FabricImage, то
      // при применении clipPath рамка тоже обрезается. А группа не может иметь рамку
      const rect = new fabric.Rect({
        strokeWidth: props.strokeWidth,
        stroke: props.stroke,
        strokeDashArray: props.strokeDashArray,
        strokeUniform: props.strokeUniform,
        width: cropWidth,
        height: cropHeight,
        rx: props.rx,
        ry: props.ry,
        flipX: object.object.flipX,
        flipY: object.object.flipY,
        fill: null,
        opacity: 1,
      })

      // Изображение. Не используем Rect, так как у него нет поля фильтров. Но у изображения нет параметров offset,
      // как у Pattern, так что обрезаем его с помощью clipPath. В ImageObject не используем FabricImage, так как при
      // применении clipPath на границе изображение теряет в качестве. Но здесь решили это допустить ради оптимизации.
      const offsetX = (props.fill as fabricTypes.Pattern).offsetX
      const offsetY = (props.fill as fabricTypes.Pattern).offsetY
      this.image = image
      this.image.set({
        clipPath: new fabric.Rect({
          width: cropWidth,
          height: cropHeight,
          left: -object.image.width / 2 - offsetX,
          top: -object.image.height / 2 - offsetY,
          rx: props.rx,
          ry: props.ry,
        }),
        width: object.image.width,
        height: object.image.height,
        flipX: object.object.flipX,
        flipY: object.object.flipY,
      })

      // Группа. Она нужна в первую очередь для общей тени
      const position = object.object.getPointByOrigin('left', 'top')
      this.group = new fabric.Group([ this.image, rect ], {
        scaleX: props.scaleX,
        scaleY: props.scaleY,
        left: position.x,
        top: position.y,
        shadow: props.shadow,
        selectable: false,
        evented: false,
        opacity: props.opacity,
      })

      this.image.applyFilters(this.getFilers(object))
      object.object.canvas?.add(this.group)
      object.object.canvas?.moveObjectTo(this.group, object.object.canvas.getObjects().indexOf(object.object) + 1)

      // Центрируем рамку, тут никаких доп условий нет
      rect.setPositionByOrigin(new fabric.Point(0, 0), 'center', 'center')

      // Здесь вычисляем разницу между оригинальной высотой и высотой обрезанной картинки (так как картинка несмотря на
      // clipPath имеет размеры оригинального изображения), чтобы потом на это значение сдвинуть картинку вниз и вправо,
      // чтобы точка отсчета координат для изображения была там же, где и в обычном состоянии (потом еще применятся
      // отступы, если картинка обрезана с отступами сверху/слева, здесь это не учитываем). Делим на два, так как мы
      // будем задавать координаты от центра картинки.
      const diffSizeX = (object.image.width - cropWidth) / 2
      const diffSizeY = (object.image.height - cropHeight) / 2

      // Центрируем изображение + не забываем сместить его относительно центра на отступы кадрирования. Напоминалка:
      // offsetX и offsetY - это отступы слева и сверху, отрицательные числа чаще всего, поэтому когда мы имеем отступы
      // слева/сверху - мы смещаем картинку влево/вверх. При отражении надо двигать в другую сторону.
      this.image.setPositionByOrigin(new fabric.Point(
        (diffSizeX + offsetX) * (object.object.flipX ? -1 : 1),
        (diffSizeY + offsetY) * (object.object.flipY ? -1 : 1),
      ), 'center', 'center')

      // Если обводка не скейлится, то для того, чтобы она была такого же размера после применения скейла к группе
      // - надо ее разделить на скейл группы. Но фабрика делает это автоматически, здесь мы вычисляем эти значения,
      // чтоб посчитать реальную высоту группы до скейла
      const strokeXBeforeScale = props.strokeUniform ? props.strokeWidth! / props.scaleX! : props.strokeWidth!
      const strokeYBeforeScale = props.strokeUniform ? props.strokeWidth! / props.scaleY! : props.strokeWidth!

      // Применяем к группе угол после того, как позиционировали все элементы, иначе нам бы пришлось во всех предыдущих
      // расчетах учитывать угол наклона. А также здесь жестко фиксируем ширину и высоту, иначе он берет высоту/ширину
      // больше чем надо при наличии кадрирования: так как this.image имеет на самом деле цельные размеры (как до
      // кадрирования), просто видимая область обрезана, группа это видит и принимает ее ширину/высоту и поэтому
      // центрирование элементов сбивается. Применяем после всех правок так как при любых изменениях внутри группы
      // фабрика пересчитывает координаты группы и раньше задавать размеры бессмысленно.
      this.group.set({
        angle: object.object.angle,
        width: cropWidth + strokeXBeforeScale,
        height: cropHeight + strokeYBeforeScale,
      })

      object.object.canvas?.renderAll()
      if (this.removeAfterCreating) {
        this.onRemove(object)
      }
    })
  }

  onAfterSet = (object: EditorObject): void => {
    if (!this.image || !(object instanceof ImageObject)) {
      return
    }
    this.image.set('filters', this.getFilers(object))
    this.image.applyFilters()
  }

  onRemove(object: ImageObject): void {
    if (!this.image || !this.group || !this.group.canvas) {
      this.removeAfterCreating = true
      return
    }

    const image = <HTMLImageElement>this.image.getElement()
    object.image = image
    object.object.visible = true
    ;(object.object.fill as fabricTypes.Pattern).source = image

    this.group.canvas.remove(this.group)
  }

  private getFilers(object: ImageObject): fabricTypes.filters.BaseFilter<string>[] {
    const filters: fabricTypes.filters.BaseFilter<string>[] = []
    if (object.brightness !== 0) {
      filters.push(new fabric.filters.Brightness({
        brightness: object.calculateCorrectionFromValue(object.brightness),
      }))
    }
    if (object.contrast !== 0) {
      filters.push(new fabric.filters.Contrast({
        contrast: object.calculateCorrectionFromValue(object.contrast),
      }))
    }
    if (object.saturation !== 0) {
      filters.push(new fabric.filters.Saturation({
        saturation: object.calculateCorrectionFromValue(object.saturation),
      }))
    }
    if (object.colorfulness !== 0) {
      filters.push(new fabric.filters.Vibrance({
        vibrance: object.calculateCorrectionFromValue(object.colorfulness),
      }))
    }
    if (object.hue !== 0) {
      filters.push(new fabric.filters.HueRotation({
        rotation: object.calculateCorrectionFromValue(object.hue),
      }))
    }
    return filters
  }
}

export class BackRemovingState extends ObjectState {
  unwatch: WatchStopHandle | null = null
  unwatchError: WatchStopHandle | null = null

  onAdd(object: ImageObject): void {
    const imagesStore = useImageStore()
    imagesStore.removeBack(object.imageUrl).then((uuid) => {
      this.unwatch = watch(() => {
        return imagesStore.images[uuid]
      }, (replacedImage) => {
        if (!replacedImage) {
          return
        }
        const oldData = {
          imageUrl: object.imageUrl,
          backRemoved: object.backRemoved,
          preview: object.preview ?? undefined,
        }
        const newData = {
          imageUrl: replacedImage,
          backRemoved: true,
        }
        object.set(newData).then(() => {
          imagesStore.loadingProcessType = undefined
          object.removeState(ImageState.RemoveBackground)
          object.object.canvas?.renderAll()
          object.object.canvas?.fire('object:modified', {
            action: Action.ReplaceImage,
            target: object.object,
            modification: [ {
              id: object.id,
              oldProps: oldData,
              newProps: newData,
            } ],
          })
          this.unwatch?.()
          this.unwatchError?.()
        })
      })

      this.unwatchError = watch(() => {
        return imagesStore.isBackRemovingError
      }, (isError) => {
        if (isError) {
          object.removeState(ImageState.RemoveBackground)
          object.object.canvas?.fire('object:modified', {
            target: object.object,
          })
          this.unwatch?.()
          this.unwatchError?.()
        }
      })
    }).catch(() => {
      object.removeState(ImageState.RemoveBackground)
      object.object.canvas?.fire('object:modified', {
        target: object.object,
      })
    })
  }
  onRemove(): void {
    this.unwatch?.()
    this.unwatchError?.()
  }
  needRemoveOnFocusout() {
    return false
  }
}

export class EnhanceImageState extends ObjectState {
  unwatch: WatchStopHandle | null = null

  onAdd(object: ImageObject): void {
    if ((object.originalWidth * object.originalHeight) >= 5000000) {
      useNuxtApp().$event('popup:show', {
        text: 'Улучшение качества доступно с изображениями до 5 Мп',
      })
      object.removeState(ImageState.EnhanceImage)
      return
    }

    const imagesStore = useImageStore()
    const auth = useAuth()

    imagesStore.enhanceImage(object.imageUrl, object.backRemoved ?? false)
      .then(({ taskUuid, generations }) => {
        this.unwatch = watch(() => {
          return imagesStore.enhanceResults[taskUuid]
        }, (result) => {
          if (!result) {
            return
          }

          if (result.status === BackgroundGenerationStatus.Error || result.urls.length === 0) {
            useNuxtApp().$event('popup:show', {
              text: 'Не удалось улучшить изображение.',
            })
            object.removeState(ImageState.EnhanceImage)
            object.object.canvas?.fire('object:modified', {
              target: object.object,
            })
            return
          }

          auth.setGenerations(generations)
          auth.subGeneration()

          this.applyEnhancedImage(result.urls[0], object)
        }, { immediate: true })
      })
      .catch(() => {
        useNuxtApp().$event('popup:show', {
          text: 'Не удалось улучшить изображение.',
        })
        object.removeState(ImageState.EnhanceImage)
        object.object.canvas?.fire('object:modified', {
          target: object.object,
        })
      })
  }

  applyEnhancedImage(enhancedUrl: string, object: ImageObject): void {
    const img = new Image()
    img.src = enhancedUrl
    img.onload = () => {
      const newOriginalWidth = img.width
      const newOriginalHeight = img.height

      const scaleFactorX = newOriginalWidth / object.originalWidth
      const scaleFactorY = newOriginalHeight / object.originalHeight

      const oldData = {
        ...object.getCanvasManagedProperties(),
        imageUrl: object.imageUrl,
        originalWidth: object.originalWidth,
        originalHeight: object.originalHeight,
        cropWidth: object.cropWidth,
        cropHeight: object.cropHeight,
        offsetX: object.offsetX,
        offsetY: object.offsetY,
        isEnhanced: object.isEnhanced,
      }
      const newData = {
        imageUrl: enhancedUrl,
        originalWidth: newOriginalWidth,
        originalHeight: newOriginalHeight,
        cropWidth: Math.round(object.cropWidth * scaleFactorX),
        cropHeight: Math.round(object.cropHeight * scaleFactorY),
        offsetX: Math.round(object.offsetX * scaleFactorX),
        offsetY: Math.round(object.offsetY * scaleFactorY),
        isEnhanced: true,
      }
      object.set(newData).then(() => {
        object.removeState(ImageState.EnhanceImage)
        object.object.canvas?.renderAll()
        object.object.canvas?.fire('object:modified', {
          action: Action.ReplaceImage,
          target: object.object,
          modification: [ {
            id: object.id,
            oldProps: oldData,
            newProps: {
              ...object.getCanvasManagedProperties(),
              ...newData,
            },
          } ],
        })
      }).catch(() => {
        useNuxtApp().$event('popup:show', {
          text: 'Не удалось применить улучшенное изображение.',
        })
        object.removeState(ImageState.EnhanceImage)
        object.object.canvas?.fire('object:modified', {
          target: object.object,
        })
      })
    }
    img.onerror = () => {
      useNuxtApp().$event('popup:show', {
        text: 'Не удалось загрузить улучшенное изображение.',
      })
      object.removeState(ImageState.EnhanceImage)
      object.object.canvas?.fire('object:modified', {
        target: object.object,
      })
    }
  }

  onRemove(): void {
    this.unwatch?.()
  }

  needRemoveOnFocusout() {
    return false
  }
}

export class CropState extends ObjectState {
  crop: boolean = false
  bluredRect: fabricTypes.Rect | null = null
  visibleRect: fabricTypes.Rect | null = null
  oldData: AnyActualProp = {}

  onAdd(object: ImageObject) {
    this.oldData = {
      ...object.getCanvasManagedProperties(),
      cropWidth: object.cropWidth,
      cropHeight: object.cropHeight,
      offsetX: object.offsetX,
      offsetY: object.offsetY,
    }
    // Constants for calculation points position before roatating
    const bluredMatrix = object.object.calcTransformMatrix()
    const angleRadians = -(object.object.angle ?? 0) * (Math.PI / 180)
    const rotatePoint = new fabric.Point(bluredMatrix[4], bluredMatrix[5])

    // Get top/left of resizing object before rotating
    const tlControlObject = object.object.getPointByOrigin('left', 'top')
    const tlControlOriginObject = tlControlObject.rotate(angleRadians, rotatePoint)

    // Scale for cropping object
    const scaleXFactor = object.object.scaleX ?? 1
    const scaleYFactor = object.object.scaleY ?? 1

    let brControlOriginObject = new fabric.Point(0, 0)
    if (object.object.flipX || object.object.flipY) {
      // Get bottom/right of resizing object before rotating
      const brControlObject = object.object.getPointByOrigin('right', 'bottom')
      brControlOriginObject = brControlObject.rotate(angleRadians, rotatePoint)
    }

    const offsetX = object.offsetX * object.image.width / object.originalWidth
    const offsetY = object.offsetY * object.image.height / object.originalHeight

    // Calculate top/left of original image
    const originalImageLeft = object.object.flipX ?
      (
        tlControlOriginObject.x -
        offsetX * scaleXFactor -
        object.image.width * scaleXFactor +
        object.object.getScaledWidth()
      ) :
      (tlControlOriginObject.x + offsetX * scaleXFactor)
    const originalImageTop = object.object.flipY ?
      (
        tlControlOriginObject.y -
        offsetY * scaleYFactor -
        object.image.height * scaleYFactor +
        object.object.getScaledHeight()
      ) :
      (tlControlOriginObject.y + offsetY * scaleYFactor)
    // Rotate original image tl control like object
    const tlControlOriginalImage =
      (new fabric.Point(originalImageLeft, originalImageTop)).rotate(-angleRadians, rotatePoint)

    this.crop = true
    this.bluredRect = new fabric.Rect({
      width: object.image.width,
      height: object.image.height,
      left: tlControlOriginalImage.x,
      top: tlControlOriginalImage.y,
      scaleX: object.object.scaleX,
      scaleY: object.object.scaleY,
      flipX: object.object.flipX,
      flipY: object.object.flipY,
      selectable: false,
      evented: false,
      angle: object.object.angle,
      opacity: 0.5,
      fill: new fabric.Pattern({
        source: object.image,
        repeat: 'no-repeat',
      }),
    })

    this.visibleRect = new fabric.Rect({
      width: object.image.width,
      height: object.image.height,
      left: tlControlOriginalImage.x,
      top: tlControlOriginalImage.y,
      scaleX: object.object.scaleX,
      scaleY: object.object.scaleY,
      flipX: object.object.flipX,
      flipY: object.object.flipY,
      selectable: false,
      evented: false,
      angle: object.object.angle,
      fill: new fabric.Pattern({
        source: object.image,
        repeat: 'no-repeat',
      }),
      clipPath: new fabric.Rect({
        width: object.object.width,
        height: object.object.height,
        left: object.object.flipX ?
          ((-brControlOriginObject.x + originalImageLeft) / scaleXFactor + this.bluredRect.width / 2) :
          ((tlControlOriginObject.x - originalImageLeft) / scaleXFactor - this.bluredRect.width / 2),
        top: object.object.flipY ?
          ((-brControlOriginObject.y + originalImageTop) / scaleYFactor + this.bluredRect.height / 2) :
          ((tlControlOriginObject.y - originalImageTop) / scaleYFactor - this.bluredRect.height / 2),
      }),
    })
    object.object.set({ fill: undefined })
    this.enableCropControls(object)
    ;[ this.bluredRect, this.visibleRect ].forEach((rect) => {
      object.object.canvas?.add(rect)
      object.object.canvas?.moveObjectTo(rect, object.object.canvas.getObjects().length)
    })
    object.object.canvas?.renderAll()
    object.object.canvas?.fire('object:modified', {
      target: object.object,
    })
  }

  onScaling = (object: EditorObject) => {
    this.onCropModified(object as ImageObject)
  }

  onMoving = (object: EditorObject) => {
    this.onCropModified(object as ImageObject)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCropModified(object: ImageObject) {
    if (!this.visibleRect || !this.bluredRect) {
      return
    }

    // Constants for calculation points position before roatating
    const bluredMatrix = this.bluredRect.calcTransformMatrix()
    const angleRadians = -(object.object.angle ?? 0) * (Math.PI / 180)
    const rotatePoint = new fabric.Point(bluredMatrix[4], bluredMatrix[5])

    // Get top/left of resizing object before rotating
    const tlControlObject = object.object.getPointByOrigin('left', 'top')
    const tlControlOriginObject = tlControlObject.rotate(angleRadians, rotatePoint)

    // Get top/left of full image before rotating
    const tlControlImage = this.visibleRect.getPointByOrigin('left', 'top')
    const tlControlOriginalImage = tlControlImage.rotate(angleRadians, rotatePoint)

    // Scale for cropping object
    const scaleXFactor = this.visibleRect.scaleX
    const scaleYFactor = this.visibleRect.scaleY

    let brControlOriginObject = new fabric.Point(0, 0)
    let brControlOriginalImage = new fabric.Point(0, 0)
    if (object.object.flipX || object.object.flipY) {
      // Get bottom/right of resizing object before rotating
      const brControlObject = object.object.getPointByOrigin('right', 'bottom')
      brControlOriginObject = brControlObject.rotate(angleRadians, rotatePoint)
      // Get bottom/right of full image before rotating
      const brControlImage = this.visibleRect.getPointByOrigin('right', 'bottom')
      brControlOriginalImage = brControlImage.rotate(angleRadians, rotatePoint)
    }

    this.visibleRect.set({
      clipPath: new fabric.Rect({
        width: object.object.width,
        height: object.object.height,
        scaleX: object.object.scaleX / scaleXFactor,
        scaleY: object.object.scaleY / scaleYFactor,
        left: object.object.flipX ?
          ((brControlOriginalImage.x - brControlOriginObject.x) / scaleXFactor - this.bluredRect.width / 2) :
          ((tlControlOriginObject.x - tlControlOriginalImage.x) / scaleXFactor - this.bluredRect.width / 2),
        top: object.object.flipY ?
          ((brControlOriginalImage.y - brControlOriginObject.y) / scaleYFactor - this.bluredRect.height / 2) :
          ((tlControlOriginObject.y - tlControlOriginalImage.y) / scaleYFactor - this.bluredRect.height / 2),
      }),
    })

    object.object.canvas?.renderAll()
  }

  onRemove(object: ImageObject) {
    if (!this.visibleRect || !this.bluredRect) {
      return []
    }

    // Scale for cropping object
    const scaleXFactor = this.visibleRect.scaleX ?? 1
    const scaleYFactor = this.visibleRect.scaleY ?? 1

    this.crop = false
    this.disableCropControls(object)
    object.cropWidth = object.object.getScaledWidth() / scaleXFactor * object.originalWidth / object.image.width
    object.cropHeight = object.object.getScaledHeight() / scaleYFactor * object.originalHeight / object.image.height
    object.width = parseInt('' + object.getRealObjectWidth())
    object.height = parseInt('' + object.getRealObjectHeight())

    // Constants for calculation points position before roatating
    const bluredMatrix = this.bluredRect.calcTransformMatrix()
    const angleRadians = -(object.object.angle ?? 0) * (Math.PI / 180)
    const rotatePoint = new fabric.Point(bluredMatrix[4], bluredMatrix[5])

    // Get top/left of resizing object before rotating
    const tlControlObject = object.object.getPointByOrigin('left', 'top')
    const tlControlOriginObject = tlControlObject.rotate(angleRadians, rotatePoint)

    // Get top/left of full image before rotating
    const tlControlImage = this.visibleRect.getPointByOrigin('left', 'top')
    const tlControlOriginalImage = tlControlImage.rotate(angleRadians, rotatePoint)

    let brControlOriginObject = new fabric.Point(0, 0)
    let brControlOriginalImage = new fabric.Point(0, 0)
    if (object.object.flipX || object.object.flipY) {
      // Get bottom/right of resizing object before rotating
      const brControlObject = object.object.getPointByOrigin('right', 'bottom')
      brControlOriginObject = brControlObject.rotate(angleRadians, rotatePoint)
      // Get bottom/right of full image before rotating
      const brControlImage = this.visibleRect.getPointByOrigin('right', 'bottom')
      brControlOriginalImage = brControlImage.rotate(angleRadians, rotatePoint)
    }

    object.offsetX = (
      object.object.flipX ?
        ((brControlOriginObject.x - brControlOriginalImage.x) / scaleXFactor + 1) :
        ((-tlControlOriginObject.x + tlControlOriginalImage.x) / scaleXFactor)
    ) * object.originalWidth / object.image.width
    object.offsetY = (
      object.object.flipY ?
        ((brControlOriginObject.y - brControlOriginalImage.y) / scaleYFactor + 1) :
        ((-tlControlOriginObject.y + tlControlOriginalImage.y) / scaleYFactor)
    ) * object.originalHeight / object.image.height

    const props = object.getFabricObjectProps()

    object.object.set({
      width: props.width,
      height: props.height,
      scaleX: props.scaleX,
      scaleY: props.scaleY,
      rx: props.rx,
      ry: props.ry,
      strokeWidth: props.strokeWidth,
      shadow: props.shadow,
      fill: props.fill,
    })

    ;[ this.bluredRect, this.visibleRect ].forEach((rect) => {
      object.object.canvas?.remove(rect)
    })
    object.object.canvas?.renderAll()
    object.afterScaling()
    object.afterModified()
    object.object.canvas?.fire('object:modified', {
      action: Action.Crop,
      target: object.object,
      modification: [ {
        id: object.id,
        oldProps: this.oldData,
        newProps: {
          ...object.getCanvasManagedProperties(),
          cropWidth: object.cropWidth,
          cropHeight: object.cropHeight,
          offsetX: object.offsetX,
          offsetY: object.offsetY,
        },
      } ],
    })
  }

  originalRenders: {
    [key: string]: (
      ctx: CanvasRenderingContext2D,
      left: number,
      top: number,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      styleOverride: any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fabricObject: any,
    ) => void
  } = {}

  enableCropControls(object: ImageObject) {
    object.object.cornerColor = '#6610F2'
    object.object.transparentCorners = false
    object.object.lockRotation = true
    object.object.controls.mtr.visible = false
    this.originalRenders.mt = object.object.controls.mt.render
    const angle = object.object.angle ?? 0
    object.object.controls.mt.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(-5, 0, 10, 4)
      ctx.fill()
      ctx.restore()
    }
    this.originalRenders.mb = object.object.controls.mb.render
    object.object.controls.mb.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(-5, -4, 10, 4)
      ctx.fill()
      ctx.restore()
    }
    this.originalRenders.mr = object.object.controls.mr.render
    object.object.controls.mr.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(-4, -5, 4, 10)
      ctx.fill()
      ctx.restore()
    }
    this.originalRenders.ml = object.object.controls.ml.render
    object.object.controls.ml.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(0, -5, 4, 10)
      ctx.fill()
      ctx.restore()
    }
    this.originalRenders.tr = object.object.controls.tr.render
    object.object.controls.tr.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(-8, 0, 8, 8)
      ctx.fill()
      ctx.restore()
    }
    this.originalRenders.tl = object.object.controls.tl.render
    object.object.controls.tl.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(0, 0, 8, 8)
      ctx.fill()
      ctx.restore()
    }
    this.originalRenders.br = object.object.controls.br.render
    object.object.controls.br.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(-8, -8, 8, 8)
      ctx.fill()
      ctx.restore()
    }
    this.originalRenders.bl = object.object.controls.bl.render
    object.object.controls.bl.render = function (ctx: CanvasRenderingContext2D, left: number, top: number) {
      ctx.save()
      ctx.translate(left, top)
      ctx.rotate(fabric.util.degreesToRadians(angle))
      ctx.rect(0, -8, 8, 8)
      ctx.fill()
      ctx.restore()
    }
  }

  disableCropControls(object: ImageObject) {
    object.object.cornerColor = '#FFFFFF'
    object.object.cornerStrokeColor = '#6610F2'
    object.object.borderColor = '#6610F2'
    object.object.transparentCorners = false
    object.object.cornerSize = 10
    object.object.cornerStyle = 'circle'
    object.object.borderOpacityWhenMoving = 0.4
    object.object.lockRotation = false
    object.object.controls.mtr.visible = true
    object.object.controls.mt.render = this.originalRenders.mt
    object.object.controls.mb.render = this.originalRenders.mb
    object.object.controls.ml.render = this.originalRenders.ml
    object.object.controls.mr.render = this.originalRenders.mr
    object.object.controls.tr.render = this.originalRenders.tr
    object.object.controls.tl.render = this.originalRenders.tl
    object.object.controls.br.render = this.originalRenders.br
    object.object.controls.bl.render = this.originalRenders.bl
  }
}
